/** Live data for wallets, supports, loans and tasks. */
import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where, limit, type QueryConstraint, type Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { BoxTxDoc } from '@/lib/data';

function useLive<T>(enabled: boolean, col: string, cons: QueryConstraint[], deps: unknown[]): { data: T[]; loading: boolean } {
  const [s, setS] = useState<{ data: T[]; loading: boolean }>({ data: [], loading: enabled });
  useEffect(() => {
    if (!enabled) { setS({ data: [], loading: false }); return; }
    return onSnapshot(query(collection(db, col), ...cons),
      (snap) => setS({ data: snap.docs.map((d) => ({ ...(d.data() as object), id: d.id }) as T), loading: false }),
      () => setS({ data: [], loading: false }));
  }, [enabled, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return s;
}

export interface WalletDoc {
  id: string; name: string; active: boolean; note: string; startDateKey?: string | null; openingBalance?: number; nedarimFeePct?: number;
  stats?: { income: number; expense: number; balance: number; count: number; opening?: number; startDateKey?: string | null; before?: number;
    nedarimIncome?: number; feePct?: number; fee?: number;
    /** part of income / expense from the treasurer's box (owner 2026-11-01) — its rows are private */
    treasurerIncome?: number; treasurerExpense?: number };
}
export const useWallets = (enabled = true) => {
  const r = useLive<WalletDoc>(enabled, 'wallets', [], []);
  return { ...r, data: [...r.data].sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name, 'he')) };
};
/** A wallet's newest 500 movements (owner 2026-10-06, speed — a wallet fed by a Nedarim mosad holds years of donations). */
export const WALLET_TX_LIMIT = 500;
/** …or only its income / only its expenses (the "נכנס" / "יצא" cards, owner 2026-10-07). */
export const useWalletTx = (walletId: string | null, type?: 'income' | 'expense') =>
  useLive<BoxTxDoc & { walletId?: string }>(!!walletId, 'boxTransactions', walletId
    ? [where('walletId', '==', walletId), ...(type ? [where('type', '==', type)] : []), orderBy('dateKey', 'desc'), limit(WALLET_TX_LIMIT)] : [], [walletId, type]);

export interface SupportDoc {
  id: string; personId: string | null; recipientName: string; amount: number; dateKey: string; hebrewDate: string; box: 'bank' | 'cash';
  method: string; reference: string; walletId: string | null; note: string; status: 'active' | 'cancelled'; boxTransactionId: string | null;
  receipt: { received: boolean; dateKey: string | null; note: string; filePath: string | null };
  cancel?: { reason: string };
}
export const useSupports = (enabled = true) => useLive<SupportDoc>(enabled, 'supports', [orderBy('dateKey', 'desc'), limit(2000)], []);

export interface InstallmentDoc { id: string; dateKey: string; amount: number; interest: number; paid: boolean; paidDateKey?: string | null; paidAmount?: number; paidInterest?: number; via?: 'manual' | 'bank' | null; extra?: boolean }
export interface LoanDoc {
  id: string; lender: string; principal: number; receivedDateKey: string; box: 'bank' | 'cash'; notes: string; schedule: InstallmentDoc[]; openEnded?: boolean;
  remaining: number; paidPrincipal: number; nextDateKey: string | null; nextAmount: number | null; status: 'open' | 'closed';
}
export const useLoans = (enabled = true) => useLive<LoanDoc>(enabled, 'loans', [], []);

export interface TaskDoc {
  id: string; title: string; notes: string; dueDateKey: string | null; assigneeUid: string | null; personId: string | null;
  status: 'open' | 'done' | 'cancelled'; doneBy?: string; doneAt?: Timestamp; createdBy?: string; updatedAt?: Timestamp;
}
export const useTasks = (enabled: boolean, status: 'open' | 'all') =>
  useLive<TaskDoc>(enabled, 'tasks', status === 'open' ? [where('status', '==', 'open')] : [limit(500)], [status]);
